// ============================================================================
// PATHWAY ENGINE - Decision Logic for 6-Screen Referral Flow
// 
// This engine computes referral decisions based on:
//   1. Missing required fields (MRD logic) -> not_yet
//   2. Triggered red flags -> urgent (always overrides)
//   3. Complete with no red flags -> routine
// 
// The engine is PURE - it takes inputs and returns outputs with no side effects.
// All Firestore persistence happens in store.referrals.js
// ============================================================================

import { OutcomeStatus, normalizeOutcomeStatus } from './schemas.js';

// ============================================================================
// ENGINE VERSION
// ============================================================================

export const PATHWAY_ENGINE_VERSION = '2.0.0';

// ============================================================================
// FIELD EVALUATION HELPERS
// ============================================================================

/**
 * Check if a field value is considered "filled"
 * Empty strings, null, undefined, and empty arrays are NOT filled
 */
export const isFieldFilled = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string' && value.trim() === '') return false;
  if (Array.isArray(value) && value.length === 0) return false;
  return true;
};

/**
 * Get list of missing required field IDs
 * @param {Object} pack - Normalized EnginePack
 * @param {Object} answers - Field answers keyed by field ID
 * @returns {string[]} Array of missing required field IDs
 */
export const getMissingRequiredFieldIds = (pack, answers) => {
  if (!pack?.fields || !Array.isArray(pack.fields)) {
    return [];
  }
  
  const missing = [];
  
  for (const field of pack.fields) {
    if (field.required && !isFieldFilled(answers[field.id])) {
      missing.push(field.id);
    }
  }
  
  return missing;
};

/**
 * Get details of missing required fields (for UI display)
 * @param {Object} pack - Normalized EnginePack
 * @param {Object} answers - Field answers keyed by field ID
 * @returns {Array<{id: string, label: string}>}
 */
export const getMissingRequiredFields = (pack, answers) => {
  if (!pack?.fields || !Array.isArray(pack.fields)) {
    return [];
  }
  
  const missing = [];
  
  for (const field of pack.fields) {
    if (field.required && !isFieldFilled(answers[field.id])) {
      missing.push({
        id: field.id,
        label: field.label || field.id,
      });
    }
  }
  
  return missing;
};

/**
 * Calculate completeness score (0-100)
 * @param {Object} pack - Normalized EnginePack
 * @param {Object} answers - Field answers
 * @returns {number} Completeness percentage
 */
export const calculateCompleteness = (pack, answers) => {
  if (!pack?.fields || pack.fields.length === 0) {
    return 100; // No fields = 100% complete
  }
  
  const requiredFields = pack.fields.filter(f => f.required);
  if (requiredFields.length === 0) {
    return 100; // No required fields = 100% complete
  }
  
  const filledRequired = requiredFields.filter(f => isFieldFilled(answers[f.id]));
  return Math.round((filledRequired.length / requiredFields.length) * 100);
};

// ============================================================================
// RED FLAG EVALUATION
// ============================================================================

/**
 * Get triggered red flag details
 * @param {Object} pack - Normalized EnginePack
 * @param {string[]} selectedRedFlagIds - IDs of selected red flags
 * @returns {Array<{id: string, label: string, forceUrgent: boolean}>}
 */
export const getTriggeredRedFlags = (pack, selectedRedFlagIds) => {
  if (!pack?.redFlags || !Array.isArray(pack.redFlags)) {
    return [];
  }
  
  if (!selectedRedFlagIds || !Array.isArray(selectedRedFlagIds)) {
    return [];
  }
  
  return pack.redFlags
    .filter(rf => selectedRedFlagIds.includes(rf.id))
    .map(rf => ({
      id: rf.id,
      label: rf.label,
      description: rf.description,
      forceUrgent: rf.forceUrgent !== false,
    }));
};

/**
 * Check if any triggered red flag forces urgent
 * @param {Object} pack - Normalized EnginePack
 * @param {string[]} selectedRedFlagIds - IDs of selected red flags
 * @returns {boolean}
 */
export const hasUrgentRedFlag = (pack, selectedRedFlagIds) => {
  const triggered = getTriggeredRedFlags(pack, selectedRedFlagIds);
  return triggered.some(rf => rf.forceUrgent);
};

// ============================================================================
// REDIRECT FLAG EVALUATION (pre-outcome routing check)
//
// Evaluation order is strict and canonical:
//   1. intraSpecialtyRedirectFlags — redirect to another pack in same specialty
//   2. crossSpecialtyRedirectFlags — redirect to a different specialty
//
// If either layer matches, the Gate stops and shows a redirect result.
// No referral Form is written.
// ============================================================================

/**
 * Get triggered intra-specialty redirect flags.
 * Intra flags answer: "Should this stay in the same specialty but go to a different pack?"
 *
 * @param {Object} pack - Normalized EnginePack (must have intraSpecialtyRedirectFlags[])
 * @param {string[]} selectedIds - IDs the GP has checked
 * @returns {Array}
 */
export const getTriggeredIntraRedirects = (pack, selectedIds) => {
  if (!Array.isArray(pack?.intraSpecialtyRedirectFlags)) return [];
  if (!Array.isArray(selectedIds)) return [];
  return pack.intraSpecialtyRedirectFlags.filter(rf => selectedIds.includes(rf.id));
};

/**
 * Get triggered cross-specialty redirect flags.
 * Cross flags answer: "Does this referral belong in a different specialty entirely?"
 *
 * @param {Object} pack - Normalized EnginePack (must have crossSpecialtyRedirectFlags[])
 * @param {string[]} selectedIds - IDs the GP has checked
 * @returns {Array}
 */
export const getTriggeredCrossRedirects = (pack, selectedIds) => {
  if (!Array.isArray(pack?.crossSpecialtyRedirectFlags)) return [];
  if (!Array.isArray(selectedIds)) return [];
  return pack.crossSpecialtyRedirectFlags.filter(rf => selectedIds.includes(rf.id));
};

/**
 * Evaluate both redirect layers in canonical order (intra first, cross second).
 * Returns the first matched redirect result with level metadata.
 *
 * Result shape:
 *   { matched: false, level: null, flag: null }
 *   { matched: true,  level: 'intra_specialty' | 'cross_specialty', flag: <RedirectFlag> }
 *
 * @param {Object} pack
 * @param {string[]} selectedIntraIds
 * @param {string[]} selectedCrossIds
 * @returns {{ matched: boolean, level: string|null, flag: Object|null }}
 */
export const getActiveRedirectResult = (pack, selectedIntraIds, selectedCrossIds) => {
  // Layer 1: intra-specialty (evaluated first)
  const intraHits = getTriggeredIntraRedirects(pack, selectedIntraIds);
  if (intraHits.length > 0) {
    return { matched: true, level: 'intra_specialty', flag: intraHits[0] };
  }
  // Layer 2: cross-specialty (evaluated second)
  const crossHits = getTriggeredCrossRedirects(pack, selectedCrossIds);
  if (crossHits.length > 0) {
    return { matched: true, level: 'cross_specialty', flag: crossHits[0] };
  }
  return { matched: false, level: null, flag: null };
};

/**
 * Check whether any redirect (intra or cross) is currently active.
 * Intra is always evaluated before cross.
 *
 * @param {Object} pack
 * @param {string[]} selectedIntraIds
 * @param {string[]} selectedCrossIds
 * @returns {boolean}
 */
export const hasActiveRedirect = (pack, selectedIntraIds, selectedCrossIds) => {
  return getActiveRedirectResult(pack, selectedIntraIds, selectedCrossIds).matched;
};

/**
 * Legacy shim — searches both intra and cross arrays for a given flat list of IDs.
 * Kept for backward compatibility with any code that passes a single ID array.
 * New code should call getActiveRedirectResult() directly.
 *
 * @param {Object} pack
 * @param {string[]} selectedRedirectFlagIds
 * @returns {Array}
 */
export const getTriggeredRedirectFlags = (pack, selectedRedirectFlagIds) => {
  const intra = getTriggeredIntraRedirects(pack, selectedRedirectFlagIds);
  const cross  = getTriggeredCrossRedirects(pack, selectedRedirectFlagIds);
  return [...intra, ...cross];
};

// ============================================================================
// DECISION ENGINE
// ============================================================================

/**
 * Compute referral decision based on pack, answers, and red flags
 * 
 * DECISION RULES:
 *   1. Any triggered red flag with forceUrgent=true => URGENT (overrides all)
 *   2. Missing required fields => NOT_YET
 *   3. All required fields filled, no urgent red flags => ROUTINE
 * 
 * @param {Object} params
 * @param {Object} params.pack - Normalized EnginePack
 * @param {Object} params.answers - Field answers keyed by field ID
 * @param {string[]} params.selectedRedFlagIds - IDs of selected red flags
 * @param {Object} params.patientContext - Patient context data (optional)
 * @returns {Object} Decision result
 */
export const computePathwayDecision = ({
  pack,
  answers = {},
  selectedRedFlagIds = [],
  patientContext = {},
}) => {
  const startTime = Date.now();
  const trace = [];
  const reasonCodes = [];
  
  // ========================================================================
  // VALIDATION
  // ========================================================================
  
  if (!pack) {
    return {
      ok: false,
      error: 'Pack is null or undefined',
      outcomeStatus: null,
      rationale: 'Cannot compute decision without a valid pack',
    };
  }
  
  if (!pack._normalized) {
    console.warn('[computePathwayDecision] Pack was not normalized, results may be inconsistent');
  }
  
  trace.push({
    step: 'init',
    timestamp: new Date().toISOString(),
    packId: pack.packId,
    versionId: pack.versionId,
    fieldCount: pack.fields?.length || 0,
    redFlagCount: pack.redFlags?.length || 0,
  });
  
  // ========================================================================
  // PHASE 1: Evaluate Missing Required Fields
  // ========================================================================
  
  const missingRequiredFieldIds = getMissingRequiredFieldIds(pack, answers);
  const missingRequiredFields = getMissingRequiredFields(pack, answers);
  const completenessScore = calculateCompleteness(pack, answers);
  
  trace.push({
    step: 'field_evaluation',
    timestamp: new Date().toISOString(),
    totalFields: pack.fields?.length || 0,
    requiredFields: pack.fields?.filter(f => f.required).length || 0,
    missingRequired: missingRequiredFieldIds.length,
    missingFieldIds: missingRequiredFieldIds,
    completenessScore,
  });
  
  if (missingRequiredFieldIds.length > 0) {
    missingRequiredFieldIds.forEach(id => {
      reasonCodes.push(`MISSING:${id}`);
    });
  }
  
  // ========================================================================
  // PHASE 2: Evaluate Red Flags
  // ========================================================================
  
  const triggeredRedFlags = getTriggeredRedFlags(pack, selectedRedFlagIds);
  const triggeredRedFlagIds = triggeredRedFlags.map(rf => rf.id);
  const hasUrgent = hasUrgentRedFlag(pack, selectedRedFlagIds);
  
  trace.push({
    step: 'red_flag_evaluation',
    timestamp: new Date().toISOString(),
    totalRedFlags: pack.redFlags?.length || 0,
    selectedCount: selectedRedFlagIds.length,
    triggeredCount: triggeredRedFlags.length,
    triggeredIds: triggeredRedFlagIds,
    hasUrgentRedFlag: hasUrgent,
  });
  
  if (triggeredRedFlags.length > 0) {
    triggeredRedFlags.forEach(rf => {
      reasonCodes.push(`RED_FLAG:${rf.id}`);
    });
  }
  
  // ========================================================================
  // PHASE 3: Determine Outcome
  // ========================================================================
  
  let outcomeStatus;
  let rationale;
  let rationaleSummary;
  
  // Rule 1: Red flags force urgent (highest priority)
  if (hasUrgent) {
    outcomeStatus = OutcomeStatus.URGENT;
    const urgentFlags = triggeredRedFlags.filter(rf => rf.forceUrgent);
    rationale = `Urgent referral required due to red flag(s): ${urgentFlags.map(rf => rf.label).join(', ')}`;
    rationaleSummary = 'Red flag concern detected - urgent pathway';
    reasonCodes.push('OUTCOME:URGENT_RED_FLAG');
  }
  // Rule 2: Missing required fields -> not_yet
  else if (missingRequiredFieldIds.length > 0) {
    outcomeStatus = OutcomeStatus.NOT_YET;
    rationale = `Additional workup needed. Missing required data: ${missingRequiredFields.map(f => f.label).join(', ')}`;
    rationaleSummary = 'Incomplete minimum dataset for specialist decision';
    reasonCodes.push('OUTCOME:NOT_YET_INCOMPLETE');
  }
  // Rule 3: Complete, no urgent red flags -> routine
  else {
    outcomeStatus = OutcomeStatus.ROUTINE;
    rationale = 'All required data present. No urgent red flags triggered.';
    rationaleSummary = 'Meets minimum dataset; no urgent concerns identified';
    reasonCodes.push('OUTCOME:ROUTINE_COMPLETE');
  }
  
  trace.push({
    step: 'outcome_determined',
    timestamp: new Date().toISOString(),
    outcomeStatus,
    rationale: rationaleSummary,
    reasonCodes,
  });
  
  // ========================================================================
  // PHASE 4: Build Not Yet Next Steps (if applicable)
  // ========================================================================
  
  let notYetNextSteps = [];
  
  if (outcomeStatus === OutcomeStatus.NOT_YET) {
    // Start with pack-defined next steps
    notYetNextSteps = (pack.notYetNextSteps || []).map(step => ({
      ...step,
      completed: false,
    }));
    
    // Add steps for missing fields not covered by explicit steps
    const coveredFieldIds = notYetNextSteps.flatMap(s => s.linkedFieldIds || []);
    const uncoveredMissingIds = missingRequiredFieldIds.filter(id => !coveredFieldIds.includes(id));
    
    if (uncoveredMissingIds.length > 0) {
      const uncoveredLabels = missingRequiredFields
        .filter(f => uncoveredMissingIds.includes(f.id))
        .map(f => f.label);
      
      notYetNextSteps.push({
        id: 'complete_missing_fields',
        label: 'Complete required clinical data',
        description: `Fill in: ${uncoveredLabels.join(', ')}`,
        order: notYetNextSteps.length,
        linkedFieldIds: uncoveredMissingIds,
        completed: false,
      });
    }
  }
  
  trace.push({
    step: 'next_steps_built',
    timestamp: new Date().toISOString(),
    stepCount: notYetNextSteps.length,
  });
  
  // ========================================================================
  // PHASE 5: Build Output
  // ========================================================================
  
  const endTime = Date.now();
  const durationMs = endTime - startTime;
  
  trace.push({
    step: 'complete',
    timestamp: new Date().toISOString(),
    durationMs,
  });
  
  return {
    ok: true,
    
    // Core outcome
    outcomeStatus,
    rationale,
    rationaleSummary,
    
    // Scoring
    completenessScore,
    
    // Field evaluation
    missingRequiredFieldIds,
    missingRequiredFields,
    
    // Red flag evaluation
    triggeredRedFlagIds,
    triggeredRedFlags,
    
    // Not Yet specific
    notYetNextSteps,
    canRerunGate: outcomeStatus === OutcomeStatus.NOT_YET,
    
    // Audit trace
    trace,
    reasonCodes,
    
    // Metadata
    engineVersion: PATHWAY_ENGINE_VERSION,
    durationMs,
    evaluatedAt: new Date().toISOString(),
  };
};

// ============================================================================
// OUTPUT GENERATION
// ============================================================================

/**
 * Generate patient-facing message based on outcome
 * @param {Object} pack - Normalized EnginePack
 * @param {string} outcomeStatus - 'urgent' | 'routine' | 'not_yet'
 * @returns {string} Patient message
 */
export const getPatientMessage = (pack, outcomeStatus) => {
  const templates = pack?.patientMessageTemplates || pack?.patientMessages || {};
  const status = normalizeOutcomeStatus(outcomeStatus);
  
  return templates[status] || templates.routine || 
    'Your referral has been processed. You will be contacted with next steps.';
};

/**
 * Generate referral note (for copy/paste)
 * @param {Object} params
 * @returns {string} Referral note text
 */
export const generateReferralNote = ({
  pack,
  patientContext,
  answers,
  decision,
}) => {
  const lines = [];
  
  // Header
  lines.push(`REFERRAL TO ${(pack.metadata?.specialty || pack.specialtyId || 'SPECIALIST').toUpperCase()}`);
  lines.push(`Pack: ${pack.name || pack.packId} v${pack.versionId}`);
  lines.push('');
  
  // Visit context (non-PHI only — no patient reference, name, DOB, MRN, or identifiers)
  if (patientContext) {
    lines.push('--- VISIT CONTEXT ---');
    if (patientContext.visitType) lines.push(`Visit Type: ${patientContext.visitType}`);
    if (patientContext.telehealthState) lines.push(`State: ${patientContext.telehealthState}`);
    if (patientContext.reasonForEscalation) {
      lines.push(`Reason for Escalation: ${patientContext.reasonForEscalation}`);
    }
    lines.push('');
  }
  
  // Decision
  lines.push('--- DECISION ---');
  lines.push(`Outcome: ${decision.outcomeStatus?.toUpperCase() || 'UNKNOWN'}`);
  lines.push(`Rationale: ${decision.rationale || 'N/A'}`);
  lines.push(`Completeness: ${decision.completenessScore || 0}%`);
  lines.push('');
  
  // Red flags (if any)
  if (decision.triggeredRedFlags?.length > 0) {
    lines.push('--- RED FLAGS ---');
    decision.triggeredRedFlags.forEach(rf => {
      lines.push(`• ${rf.label}`);
    });
    lines.push('');
  }
  
  // Clinical data
  if (pack.fields?.length > 0 && Object.keys(answers || {}).length > 0) {
    lines.push('--- CLINICAL DATA ---');
    pack.fields.forEach(field => {
      const value = answers[field.id];
      let displayValue = 'N/A';
      
      if (value !== null && value !== undefined) {
        if (Array.isArray(value)) {
          displayValue = value.join(', ') || 'N/A';
        } else {
          displayValue = String(value) || 'N/A';
        }
      }
      
      lines.push(`${field.label}: ${displayValue}`);
    });
    lines.push('');
  }
  
  // Footer
  lines.push('---');
  lines.push(`Generated: ${new Date().toISOString()}`);
  lines.push(`Engine: v${PATHWAY_ENGINE_VERSION}`);
  
  return lines.join('\n');
};

// ============================================================================
// SESSION RE-RUN (In-Memory Only)
// ============================================================================

/**
 * Re-run the pathway decision with updated answers
 * This is for Screen 5 "Re-run Gate" - does NOT write to Firestore
 * Just re-evaluates in memory
 * 
 * @param {Object} params - Same as computePathwayDecision
 * @returns {Object} Updated decision result
 */
export const rerunPathwayDecision = (params) => {
  // Simply re-compute - no state change, no DB write
  return computePathwayDecision(params);
};

// ============================================================================
// EXPORTS
// ============================================================================

export default {
  PATHWAY_ENGINE_VERSION,
  
  // Field evaluation
  isFieldFilled,
  getMissingRequiredFieldIds,
  getMissingRequiredFields,
  calculateCompleteness,
  
  // Red flag evaluation
  getTriggeredRedFlags,
  hasUrgentRedFlag,

  // Redirect flag evaluation (pre-outcome routing check)
  getTriggeredRedirectFlags,
  hasActiveRedirect,
  
  // Decision engine
  computePathwayDecision,
  rerunPathwayDecision,
  
  // Output generation
  getPatientMessage,
  generateReferralNote,
};
